// Desert vivarium — 3D environment + photoreal species billboards with animal motion.
import React, { useRef, useMemo } from "react";
import * as THREE from "three";
import { useFrame } from "@react-three/fiber";
import { Billboard, useTexture, Html } from "@react-three/drei";
import { SAND_TOP } from "./data";

const CUT = (id) => `/assets/vivarium/${id}-cut.png`;

export function Enclosure() {
  const glass = { color: "#bfe3ff", transparent: true, opacity: 0.06, roughness: 0.05, metalness: 0.4, side: 2, depthWrite: false };
  const frame = { color: "#2a2a2e", metalness: 0.8, roughness: 0.35 };
  const X = 6, Y = 3.5, Z = 5;
  const corners = [[-X, -Z], [X, -Z], [-X, Z], [X, Z]];
  return (
    <group>
      <mesh position={[0, 0, -Z]}><planeGeometry args={[X * 2, Y * 2]} /><meshStandardMaterial {...glass} /></mesh>
      <mesh position={[0, 0, Z]}><planeGeometry args={[X * 2, Y * 2]} /><meshStandardMaterial {...glass} /></mesh>
      <mesh position={[-X, 0, 0]} rotation={[0, Math.PI / 2, 0]}><planeGeometry args={[Z * 2, Y * 2]} /><meshStandardMaterial {...glass} /></mesh>
      <mesh position={[X, 0, 0]} rotation={[0, Math.PI / 2, 0]}><planeGeometry args={[Z * 2, Y * 2]} /><meshStandardMaterial {...glass} /></mesh>
      <mesh position={[0, Y, 0]} rotation={[Math.PI / 2, 0, 0]}><planeGeometry args={[X * 2, Z * 2]} /><meshStandardMaterial {...glass} /></mesh>
      {corners.map(([x, z]) => (
        <mesh key={`${x}${z}`} position={[x, 0, z]}><boxGeometry args={[0.14, Y * 2, 0.14]} /><meshStandardMaterial {...frame} /></mesh>
      ))}
      <mesh position={[0, Y, -Z]}><boxGeometry args={[X * 2 + 0.14, 0.14, 0.14]} /><meshStandardMaterial {...frame} /></mesh>
      <mesh position={[0, Y, Z]}><boxGeometry args={[X * 2 + 0.14, 0.14, 0.14]} /><meshStandardMaterial {...frame} /></mesh>
      <mesh position={[-X, Y, 0]}><boxGeometry args={[0.14, 0.14, Z * 2 + 0.14]} /><meshStandardMaterial {...frame} /></mesh>
      <mesh position={[X, Y, 0]}><boxGeometry args={[0.14, 0.14, Z * 2 + 0.14]} /><meshStandardMaterial {...frame} /></mesh>
    </group>
  );
}

export function Terrain() {
  const sand = useTexture("/assets/vivarium/viv-sand.png");
  sand.wrapS = sand.wrapT = THREE.RepeatWrapping;
  sand.repeat.set(4, 3.2);
  const rocks = useMemo(() => [
    { p: [2.8, SAND_TOP + 0.35, -0.4], s: [0.9, 0.7, 0.8] },
    { p: [-4.4, SAND_TOP + 0.28, -1.2], s: [0.7, 0.55, 0.6] },
    { p: [4.4, SAND_TOP + 0.22, 2.6], s: [0.55, 0.4, 0.5] },
    { p: [-1.8, SAND_TOP + 0.18, 4.0], s: [0.45, 0.35, 0.4] },
  ], []);
  return (
    <group>
      <mesh position={[0, SAND_TOP - 0.25, 0]}><boxGeometry args={[11.8, 0.5, 9.8]} /><meshStandardMaterial color="#c2a878" roughness={1} /></mesh>
      <mesh position={[0, SAND_TOP + 0.02, 0]}><boxGeometry args={[11.8, 0.06, 9.8]} /><meshStandardMaterial map={sand} color="#e8d2a8" roughness={1} /></mesh>
      {rocks.map((r, i) => (
        <mesh key={i} position={r.p} scale={r.s} rotation={[i * 0.7, i * 1.3, i * 0.4]}>
          <dodecahedronGeometry args={[1, 0]} />
          <meshStandardMaterial color="#8a7a63" roughness={0.95} flatShading />
        </mesh>
      ))}
      {/* paludarium pool */}
      <mesh position={[-3, SAND_TOP + 0.04, 2.5]} rotation={[-Math.PI / 2, 0, 0]}>
        <circleGeometry args={[1.6, 40]} />
        <meshStandardMaterial color="#3d6b8f" transparent opacity={0.75} roughness={0.1} metalness={0.6} />
      </mesh>
      <mesh position={[-3, SAND_TOP + 0.03, 2.5]} rotation={[-Math.PI / 2, 0, 0]}>
        <ringGeometry args={[1.55, 1.85, 40]} />
        <meshStandardMaterial color="#a89468" roughness={1} />
      </mesh>
    </group>
  );
}

export function SpeciesBillboard({ sp, running, selected, onSelect }) {
  const tex = useTexture(CUT(sp.img || sp.id));
  const sway = useRef();
  useFrame(({ clock }) => {
    if (running && sway.current && sp.kind !== "animal") {
      sway.current.rotation.z = Math.sin(clock.getElapsedTime() * 0.6 + sp.pos[0]) * 0.015;
    }
  });
  const aspect = tex.image ? tex.image.width / tex.image.height : 1.4;
  const w = sp.h * aspect * 0.9;
  return (
    <group position={[sp.pos[0], SAND_TOP, sp.pos[1]]} onClick={(e) => { e.stopPropagation(); onSelect(sp.id); }}>
      <group ref={sway}>
        <group position={[0, sp.h / 2, 0]}>
          <mesh><planeGeometry args={[w, sp.h]} /><meshStandardMaterial map={tex} emissiveMap={tex} emissive="#ffffff" emissiveIntensity={0.28} transparent alphaTest={0.35} side={2} roughness={0.9} /></mesh>
          <mesh rotation={[0, Math.PI / 2, 0]}><planeGeometry args={[w, sp.h]} /><meshStandardMaterial map={tex} emissiveMap={tex} emissive="#ffffff" emissiveIntensity={0.28} transparent alphaTest={0.35} side={2} roughness={0.9} /></mesh>
        </group>
      </group>
      {selected && (
        <mesh position={[0, 0.06, 0]} rotation={[-Math.PI / 2, 0, 0]}>
          <ringGeometry args={[w * 0.6, w * 0.6 + 0.06, 32]} />
          <meshBasicMaterial color="#e5c158" />
        </mesh>
      )}
      <Html position={[0, sp.h + 0.35, 0]} center distanceFactor={13} zIndexRange={[5, 0]}>
        <button data-testid={`label-${sp.id}`} onClick={(e) => { e.stopPropagation(); onSelect(sp.id); }}
          className={`font-mono text-[9px] tracking-widest px-1.5 py-0.5 rounded border whitespace-nowrap transition-colors ${
            selected ? "bg-amber-400 text-stone-950 border-amber-300" : "text-amber-200/90 bg-stone-950/70 border-amber-500/20 hover:border-amber-400"}`}>
          {sp.name}
        </button>
      </Html>
    </group>
  );
}

export function TortoiseWalker({ running }) {
  const tex = useTexture(CUT("viv-tortoise"));
  const g = useRef();
  useFrame(({ clock }) => {
    if (!running || !g.current) return;
    const t = clock.getElapsedTime() * 0.05;
    g.current.position.x = 1.4 + Math.cos(t) * 1.6;
    g.current.position.z = 1.9 + Math.sin(t) * 1.2;
    g.current.position.y = SAND_TOP + 0.55 + Math.abs(Math.sin(clock.getElapsedTime() * 1.2)) * 0.03;
  });
  const aspect = tex.image ? tex.image.width / tex.image.height : 1.6;
  return (
    <group ref={g} position={[1.4, SAND_TOP + 0.55, 1.9]}>
      <Billboard follow>
        <mesh><planeGeometry args={[1.15 * aspect * 0.7, 1.15]} /><meshStandardMaterial map={tex} emissiveMap={tex} emissive="#ffffff" emissiveIntensity={0.28} transparent alphaTest={0.35} side={2} /></mesh>
      </Billboard>
    </group>
  );
}

export function DragonflyHover({ running }) {
  const tex = useTexture(CUT("viv-dragonfly"));
  const g = useRef();
  useFrame(({ clock }) => {
    if (!g.current) return;
    const t = clock.getElapsedTime();
    const x = -3 + Math.sin(t * 0.9) * 1.1;
    const z = 2.5 + Math.sin(t * 1.8) * 0.55;
    const y = SAND_TOP + 1.5 + (running ? Math.sin(t * 7) * 0.06 : 0);
    g.current.position.set(x, y, z);
  });
  const aspect = tex.image ? tex.image.width / tex.image.height : 1.5;
  return (
    <group ref={g} position={[-3, SAND_TOP + 1.5, 2.5]}>
      <Billboard follow>
        <mesh><planeGeometry args={[0.55 * aspect, 0.55]} /><meshStandardMaterial map={tex} emissiveMap={tex} emissive="#ffffff" emissiveIntensity={0.28} transparent alphaTest={0.3} side={2} /></mesh>
      </Billboard>
    </group>
  );
}

export function ChuckwallaBask({ running }) {
  const tex = useTexture(CUT("viv-chuckwalla"));
  const g = useRef();
  useFrame(({ clock }) => {
    if (running && g.current) {
      const t = clock.getElapsedTime() % 6;
      g.current.position.y = SAND_TOP + 1.05 + (t < 1.2 ? Math.abs(Math.sin(t * Math.PI * 2.5)) * 0.08 : 0);
    }
  });
  const aspect = tex.image ? tex.image.width / tex.image.height : 1.6;
  return (
    <group ref={g} position={[2.8, SAND_TOP + 1.05, -0.4]}>
      <Billboard follow>
        <mesh><planeGeometry args={[0.95 * aspect * 0.75, 0.95]} /><meshStandardMaterial map={tex} emissiveMap={tex} emissive="#ffffff" emissiveIntensity={0.28} transparent alphaTest={0.35} side={2} /></mesh>
      </Billboard>
    </group>
  );
}

export function RoadrunnerStand({ running }) {
  const tex = useTexture(CUT("viv-roadrunner"));
  const g = useRef();
  useFrame(({ clock }) => {
    if (running && g.current) {
      const t = clock.getElapsedTime() % 4;
      g.current.rotation.z = t > 3.6 ? Math.sin((t - 3.6) * Math.PI * 5) * 0.08 : 0;
    }
  });
  const aspect = tex.image ? tex.image.width / tex.image.height : 1.3;
  return (
    <group ref={g} position={[-0.8, SAND_TOP + 0.62, 3.0]}>
      <Billboard follow>
        <mesh><planeGeometry args={[1.25 * aspect * 0.75, 1.25]} /><meshStandardMaterial map={tex} emissiveMap={tex} emissive="#ffffff" emissiveIntensity={0.28} transparent alphaTest={0.35} side={2} /></mesh>
      </Billboard>
    </group>
  );
}

export function SunShafts({ day }) {
  if (!day) return null;
  return (
    <group>
      <mesh position={[1.5, 0.6, -1]} rotation={[0.2, 0, 0.35]}>
        <boxGeometry args={[2.4, 7.5, 1.6]} />
        <meshBasicMaterial color="#ffe0a3" transparent opacity={0.05} depthWrite={false} />
      </mesh>
      <mesh position={[-2.2, 0.6, 0.8]} rotation={[-0.15, 0, 0.28]}>
        <boxGeometry args={[1.7, 7.5, 1.3]} />
        <meshBasicMaterial color="#ffedc4" transparent opacity={0.04} depthWrite={false} />
      </mesh>
    </group>
  );
}

// The dim room beyond the glass — a photographic backdrop cylinder so the enclosure
// reads as an indoor vivarium from every orbit angle, like the hero render.
export function RoomBackdrop({ day }) {
  const tex = useTexture("/assets/vivarium/viv-room.png");
  return (
    <mesh position={[0, 2.5, 0]}>
      <cylinderGeometry args={[16, 16, 18, 48, 1, true]} />
      <meshBasicMaterial map={tex} side={THREE.BackSide} fog={false} transparent opacity={day ? 0.85 : 0.35} color={day ? "#8f8578" : "#3a4054"} />
    </mesh>
  );
}

// Flood-cycle hydro rig: corner reservoir + six drip lines to the tree root zones,
// with a visible water pulse traveling each line — deep watering, infrequently.
export function HydroSystem({ running, selected, onSelect }) {
  const tank = [5.2, -3.8];
  const targets = [[-4.0, -2.5], [-1.5, -3.5], [3.8, -2.8], [1.2, -1.2], [-2.2, 0.5], [4.9, -0.9]];
  const lines = useMemo(() => targets.map(([tx, tz]) => {
    const mx = (tank[0] + tx) / 2 + (tz > tank[1] ? 0.4 : -0.4);
    return new THREE.CatmullRomCurve3([
      new THREE.Vector3(tank[0], SAND_TOP + 0.09, tank[1]),
      new THREE.Vector3(mx, SAND_TOP + 0.07, (tank[1] + tz) / 2),
      new THREE.Vector3(tx, SAND_TOP + 0.07, tz),
    ]);
  }), []);
  const pulses = useRef([]);
  useFrame(({ clock }) => {
    if (!running) return;
    const t0 = clock.getElapsedTime() * 0.1;
    pulses.current.forEach((p, i) => {
      if (!p) return;
      const t = (t0 + i * 0.17) % 1;
      const pt = lines[i].getPoint(t);
      p.position.copy(pt);
      p.material.opacity = t > 0.03 && t < 0.97 ? 0.95 : 0;
    });
  });
  return (
    <group onClick={(e) => { e.stopPropagation(); onSelect("viv-hydro"); }}>
      <mesh position={[tank[0], SAND_TOP + 0.6, tank[1]]}>
        <boxGeometry args={[1.2, 1.2, 0.9]} />
        <meshStandardMaterial color="#14161a" metalness={0.5} roughness={0.5} />
      </mesh>
      <mesh position={[tank[0], SAND_TOP + 1.24, tank[1]]}>
        <boxGeometry args={[1.26, 0.1, 0.96]} />
        <meshStandardMaterial color="#0b0b0d" metalness={0.6} roughness={0.4} />
      </mesh>
      <mesh position={[tank[0] - 0.62, SAND_TOP + 0.85, tank[1]]}>
        <boxGeometry args={[0.06, 0.3, 0.4]} />
        <meshStandardMaterial color="#38bdf8" emissive="#38bdf8" emissiveIntensity={0.9} />
      </mesh>
      {lines.map((c, i) => (
        <mesh key={i}>
          <tubeGeometry args={[c, 32, 0.025, 6]} />
          <meshStandardMaterial color="#1c1a17" roughness={0.85} />
        </mesh>
      ))}
      {lines.map((_, i) => (
        <mesh key={`p${i}`} ref={(el) => (pulses.current[i] = el)}>
          <sphereGeometry args={[0.07, 10, 10]} />
          <meshBasicMaterial color="#7dd3fc" transparent opacity={0.9} />
        </mesh>
      ))}
      {selected && (
        <mesh position={[tank[0], SAND_TOP + 0.06, tank[1]]} rotation={[-Math.PI / 2, 0, 0]}>
          <ringGeometry args={[0.85, 0.92, 32]} />
          <meshBasicMaterial color="#e5c158" />
        </mesh>
      )}
      <Html position={[tank[0], SAND_TOP + 1.8, tank[1]]} center distanceFactor={13} zIndexRange={[5, 0]}>
        <button data-testid="label-viv-hydro" onClick={(e) => { e.stopPropagation(); onSelect("viv-hydro"); }}
          className={`font-mono text-[9px] tracking-widest px-1.5 py-0.5 rounded border whitespace-nowrap transition-colors ${
            selected ? "bg-amber-400 text-stone-950 border-amber-300" : "text-sky-300/90 bg-stone-950/70 border-sky-500/30 hover:border-sky-400"}`}>
          DEEP-WATER HYDRO
        </button>
      </Html>
    </group>
  );
}


export function DustMotes({ running }) {
  const COUNT = 70;
  const seeds = useMemo(() => Array.from({ length: COUNT }, () => ({ x: (Math.random() - 0.5) * 11, y: Math.random() * 6.5 - 3.2, z: (Math.random() - 0.5) * 9, p: Math.random() * Math.PI * 2 })), []);
  const geo = useMemo(() => {
    const g = new THREE.BufferGeometry();
    g.setAttribute("position", new THREE.BufferAttribute(new Float32Array(COUNT * 3), 3));
    return g;
  }, []);
  useFrame(({ clock }) => {
    if (!running) return;
    const t = clock.getElapsedTime();
    const pos = geo.attributes.position;
    seeds.forEach((s, i) => pos.setXYZ(i, s.x + Math.sin(t * 0.2 + s.p) * 0.3, s.y + Math.sin(t * 0.15 + s.p * 2) * 0.25, s.z));
    pos.needsUpdate = true;
  });
  return (
    <points geometry={geo}>
      <pointsMaterial size={0.05} color="#ffe9c4" transparent opacity={0.5} sizeAttenuation depthWrite={false} />
    </points>
  );
}
